import React from 'react';
import { PhoneIcon, SparklesIcon } from '@heroicons/react/24/outline';

/**
 * A quién va a llamar el gestor, pintado **mientras suena**.
 *
 * Es lo que convierte los ~22 s de tono en briefing en vez de pantalla vacía.
 * Cuando el deudor dice "¿Sí?", el gestor ya sabe con quién habla, cuánto debe y
 * qué se le prometió la última vez.
 *
 * Por eso se rellena al empezar a marcar y no al descolgar: si apareciera con la
 * voz, llegaría tarde.
 */

const euros = (amount) =>
  typeof amount === 'number'
    ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount)
    : '—';

const DebtorBriefing = ({ target, ringing = false }) => {
  if (!target) {
    return (
      <div className='flex h-full items-center justify-center rounded-lg border border-dashed border-gray-200 p-10 text-center'>
        <p className='text-sm text-gray-400'>Esperando a la siguiente llamada…</p>
      </div>
    );
  }

  const { debtorName, debt, expedientId, clientName, summary, lastInteractions = [], promise } = target;

  return (
    <div className='rounded-lg border border-gray-200 bg-white p-5'>
      <div className='flex items-start justify-between gap-4'>
        <div>
          <h2 className='text-xl font-semibold text-gray-900'>{debtorName || 'Sin nombre'}</h2>
          <p className='mt-0.5 text-sm text-gray-500'>{clientName}</p>
        </div>
        <div className='text-right'>
          <p className='text-2xl font-semibold text-gray-900'>{euros(debt)}</p>
          <p className='text-xs text-gray-400'>deuda viva</p>
        </div>
      </div>

      {ringing && (
        <p className='mt-3 flex items-center gap-2 text-sm text-gray-500'>
          <PhoneIcon className='h-4 w-4 animate-pulse' aria-hidden='true' />
          Llamando…
        </p>
      )}

      {promise && (
        <div className='mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3'>
          <p className='text-xs font-medium uppercase tracking-wide text-amber-700'>Se comprometió a</p>
          <p className='mt-0.5 text-sm text-amber-900'>{promise}</p>
        </div>
      )}

      {summary && (
        <div className='mt-4 rounded-lg bg-gray-50 p-3'>
          <p className='flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500'>
            <SparklesIcon className='h-3.5 w-3.5' aria-hidden='true' />
            Resumen
          </p>
          <p className='mt-1 text-sm leading-relaxed text-gray-700'>{summary}</p>
        </div>
      )}

      {lastInteractions.length > 0 && (
        <ul className='mt-4 space-y-1.5'>
          {lastInteractions.slice(0, 3).map((item) => (
            <li key={item.id} className='flex gap-2 text-sm text-gray-600'>
              <span className='shrink-0 text-gray-400'>{item.date}</span>
              <span className='truncate'>{item.text}</span>
            </li>
          ))}
        </ul>
      )}

      {expedientId && (
        <a
          href={`/view/${expedientId}`}
          target='_blank'
          rel='noreferrer'
          className='mt-4 inline-block text-sm text-blue-600 hover:underline'
        >
          Abrir expediente
        </a>
      )}
    </div>
  );
};

export default DebtorBriefing;
